import { useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cn } from '@/lib/utils';
import { convertRowDateTime } from '@/lib/dateTime';
import {
    Loader2Icon, GavelIcon, AlertTriangleIcon,
    Undo2Icon, TimerOffIcon, TimerIcon, HourglassIcon,
    ChevronUpIcon, ChevronDownIcon,
} from 'lucide-react';
import { useBackendApi } from '@/hooks/fetch';
import {
    HistoryTableActionType, HistoryTableSearchResp,
    HistoryTableSearchType, HistoryTableSortingType,
} from '@shared/historyApiTypes';
import { useOpenActionModal } from '@/hooks/actionModal';
import { SEARCH_ANY_STRING } from './HistorySearchBox';

// ── Single row ─────────────────────────────────────────────────────────────
function HistoryRow({
    action,
    modalOpener,
}: {
    action: HistoryTableActionType;
    modalOpener: ReturnType<typeof useOpenActionModal>;
}) {
    const isBan  = action.type === 'ban';
    const accent = isBan ? '#ef4444' : '#fbbf24';

    let StatusIcon: React.ElementType | null = null;
    let statusTitle = '';
    if (action.isRevoked) {
        StatusIcon = Undo2Icon; statusTitle = 'Revoked';
    } else if (action.banExpiration === 'permanent') {
        StatusIcon = TimerOffIcon; statusTitle = 'Permanent';
    } else if (action.banExpiration === 'active') {
        StatusIcon = TimerIcon; statusTitle = 'Active ban';
    } else if (action.type === 'warn' && !action.warnAcked) {
        StatusIcon = HourglassIcon; statusTitle = 'Awaiting ack';
    }

    return (
        <div
            onClick={() => modalOpener(action.id)}
            className={cn(
                'grid items-center gap-0 border-b border-white/[0.04]',
                'cursor-pointer transition-colors duration-75 hover:bg-white/[0.025] select-none',
                action.isRevoked && 'opacity-40',
            )}
            style={{ gridTemplateColumns: '9rem 1fr 1.7fr 1fr 9rem', minHeight: 44 }}
        >
            {/* Type badge + ID + status */}
            <div className="flex items-center gap-2 px-3 py-2.5 min-w-0">
                <div
                    className="flex items-center justify-center w-5 h-5 rounded shrink-0 border"
                    style={{ background: `${accent}12`, borderColor: `${accent}22` }}
                    title={isBan ? 'Ban' : 'Warn'}
                >
                    {isBan
                        ? <GavelIcon size={11} style={{ color: accent }} />
                        : <AlertTriangleIcon size={11} style={{ color: accent }} />
                    }
                </div>
                <span className="font-mono text-[11px] font-bold tracking-wide truncate" style={{ color: accent }}>
                    {action.id}
                </span>
                {StatusIcon && (
                    <StatusIcon size={11} className="text-slate-600 shrink-0 ml-auto" title={statusTitle} />
                )}
            </div>

            {/* Player */}
            <div className="px-3 py-2.5 border-l border-white/[0.04] min-w-0">
                <span className="text-xs text-slate-200 line-clamp-1 break-all">
                    {action.playerName || <span className="text-slate-600 italic">unknown</span>}
                </span>
            </div>

            {/* Reason */}
            <div className="px-3 py-2.5 border-l border-white/[0.04] min-w-0">
                <span className="text-xs text-slate-400 line-clamp-1 break-all">{action.reason}</span>
            </div>

            {/* Author */}
            <div className="px-3 py-2.5 border-l border-white/[0.04] min-w-0">
                <span className="text-xs text-slate-500 font-mono line-clamp-1 break-all">{action.author}</span>
            </div>

            {/* Date */}
            <div className="px-3 py-2.5 border-l border-white/[0.04] min-w-0">
                <span className="text-[10px] text-slate-600 font-mono">{convertRowDateTime(action.timestamp)}</span>
            </div>
        </div>
    );
}

// ── Footer row (loading / end-of-list / error) ─────────────────────────────
function FooterRow({
    playersCount, hasReachedEnd, isFetching, loadError, retryFetch,
}: {
    playersCount: number;
    hasReachedEnd: boolean;
    loadError: string | null;
    isFetching: boolean;
    retryFetch: (_reset?: boolean) => Promise<void>;
}) {
    return (
        <div className="flex items-center justify-center px-4 py-6 text-[11px] font-mono text-slate-600">
            {isFetching ? (
                <Loader2Icon size={15} className="animate-spin text-[#00ffd1]/30" />
            ) : loadError ? (
                <span className="text-destructive">
                    Error: {loadError} —{' '}
                    <button className="underline hover:text-slate-300 transition-colors" onClick={() => retryFetch()}>
                        retry
                    </button>
                </span>
            ) : hasReachedEnd ? (
                <span>
                    {playersCount
                        ? `${playersCount.toLocaleString()} records · end of list`
                        : 'No actions found.'}
                </span>
            ) : null}
        </div>
    );
}

// ── Main table ─────────────────────────────────────────────────────────────
type HistoryTableProps = {
    search: HistoryTableSearchType;
    filterbyType: string | undefined;
    filterbyAdmin: string | undefined;
};

export default function HistoryTable({ search, filterbyType, filterbyAdmin }: HistoryTableProps) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [history, setHistory]             = useState<HistoryTableActionType[]>([]);
    const [hasReachedEnd, setHasReachedEnd] = useState(false);
    const [isFetching, setIsFetching]       = useState(false);
    const [loadError, setLoadError]         = useState<string | null>(null);
    const [sorting, setSorting]             = useState<HistoryTableSortingType>({ key: 'timestamp', desc: true });
    const [isResetting, setIsResetting]     = useState(false);
    const openActionModal = useOpenActionModal();

    const historyListingApi = useBackendApi<HistoryTableSearchResp>({
        method: 'GET',
        path: '/history/search',
        abortOnUnmount: true,
    });

    const fetchNextPage = async (resetOffset?: boolean) => {
        setIsFetching(true);
        setLoadError(null);
        if (resetOffset) setIsResetting(true);

        const handleError = (msg: string) => {
            setLoadError(msg);
            if (resetOffset) setHistory([]);
        };

        try {
            const queryParams: Record<string, string | number | boolean> = {
                sortingKey: sorting.key,
                sortingDesc: sorting.desc,
            };
            if (search.value) {
                queryParams.searchValue = search.value;
                queryParams.searchType  = search.type;
            }
            if (filterbyType && filterbyType !== SEARCH_ANY_STRING)   queryParams.filterbyType  = filterbyType;
            if (filterbyAdmin && filterbyAdmin !== SEARCH_ANY_STRING) queryParams.filterbyAdmin = filterbyAdmin;
            if (!resetOffset && history.length) {
                queryParams.offsetParam    = history[history.length - 1][sorting.key];
                queryParams.offsetActionId = history[history.length - 1].id;
            }

            const resp = await historyListingApi({ queryParams });
            if (resp === undefined)    return handleError('Request failed.');
            if ('error' in resp)       return handleError(`Request failed: ${resp.error}`);

            setLoadError(null);
            setHasReachedEnd(resp.hasReachedEnd);
            setIsResetting(false);
            setHistory(prev => resetOffset ? resp.history : [...prev, ...resp.history]);
        } catch (err) {
            handleError(`Failed: ${(err as Error).message}`);
        } finally {
            setIsFetching(false);
            setIsResetting(false);
        }
    };

    // Virtualizer
    const rowVirtualizer = useVirtualizer({
        isScrollingResetDelay: 0,
        count: history.length + 1,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 44,
        overscan: 20,
    });
    const virtualItems = rowVirtualizer.getVirtualItems();
    const totalSize    = rowVirtualizer.getTotalSize();

    // Infinite scroll
    useEffect(() => {
        if (!history.length || !virtualItems.length) return;
        const lastIdx = virtualItems[virtualItems.length - 1].index;
        if (history.length <= lastIdx && !hasReachedEnd && !isFetching) {
            fetchNextPage();
        }
    }, [history, virtualItems, hasReachedEnd, isFetching]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset on filter/sort change
    useEffect(() => {
        rowVirtualizer.scrollToIndex(0);
        fetchNextPage(true);
    }, [search, filterbyType, filterbyAdmin, sorting]); // eslint-disable-line react-hooks/exhaustive-deps

    const COL = '9rem 1fr 1.7fr 1fr 9rem';

    return (
        <div className="flex flex-col flex-1 min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.005] overflow-hidden">
            {/* Sticky header */}
            <div
                className="grid shrink-0 border-b border-white/[0.06] bg-black/30 select-none"
                style={{ gridTemplateColumns: COL }}
            >
                {(['Action', 'Player', 'Reason', 'Author'] as const).map((label, i) => (
                    <div
                        key={label}
                        className={cn(
                            'px-3 py-2.5 text-[10px] font-mono font-bold uppercase tracking-widest text-slate-600',
                            i > 0 && 'border-l border-white/[0.04]',
                        )}
                    >
                        {label}
                    </div>
                ))}
                {/* Sortable date column */}
                <button
                    onClick={() => setSorting(s => ({ key: 'timestamp', desc: !s.desc }))}
                    className="flex items-center gap-1 px-3 py-2.5 text-[10px] font-mono font-bold uppercase tracking-widest text-slate-600 border-l border-white/[0.04] hover:text-slate-300 transition-colors"
                >
                    Date
                    {sorting.desc
                        ? <ChevronDownIcon size={10} className="text-[#00ffd1]/50" />
                        : <ChevronUpIcon   size={10} className="text-[#00ffd1]/50" />
                    }
                </button>
            </div>

            {/* Virtualised list */}
            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/[0.06] hover:scrollbar-thumb-white/10 scrollbar-track-transparent"
                style={{ overflowAnchor: 'none' }}
            >
                <div
                    className={cn('relative w-full', isResetting && 'opacity-20 pointer-events-none')}
                    style={{ height: totalSize }}
                >
                    {virtualItems.map(vItem => {
                        const isLast = vItem.index > history.length - 1;
                        return (
                            <div
                                key={vItem.key}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transform: `translateY(${vItem.start}px)`,
                                }}
                            >
                                {isLast ? (
                                    <FooterRow
                                        playersCount={history.length}
                                        hasReachedEnd={hasReachedEnd}
                                        loadError={loadError}
                                        isFetching={isFetching}
                                        retryFetch={fetchNextPage}
                                    />
                                ) : (
                                    <HistoryRow
                                        action={history[vItem.index]}
                                        modalOpener={openActionModal}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
